import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ShoppingCart, User, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/studios", label: "Studio Rental" },
  { to: "/props", label: "Props" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const cartCount = items.reduce((s, i) => s + i.quantity, 0);

  const handleLogo = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-xl border-b border-zinc-200"
    >
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <Link to="/" onClick={handleLogo} className="flex items-center" data-testid="nav-logo">
          <img src="/logo.gif" alt="CFT Creations" className="h-10 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-bold underline-grow ${isActive ? "text-[#FF007F]" : "text-[#0A1128]/80 hover:text-[#0A1128]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/cart" data-testid="nav-cart" className="relative p-2 hover:bg-zinc-100 rounded-full">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#FF007F] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </Link>
          {user && user !== false ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} data-testid="nav-dashboard"
                className="text-sm font-medium px-3 py-2 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center gap-2">
                <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout"
                className="p-2 hover:bg-zinc-100 rounded-full" title="Log out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login"
              className="hidden sm:inline-block text-sm font-semibold px-4 py-2 bg-[#0A1128] text-white rounded-full hover:bg-gradient-brand transition">
              Login
            </Link>
          )}
          <Link to="/contact" data-testid="nav-cta-consult"
            className="hidden md:inline-block text-sm font-semibold px-4 py-2 bg-gradient-brand-animate text-white rounded-full hover:opacity-90 transition">
            Book Consultation
          </Link>
          <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 hover:bg-zinc-100 rounded-full">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-200 bg-white">
          <div className="px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                data-testid={`m-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base font-medium text-[#0A1128]/80 hover:text-[#FF007F]">
                {l.label}
              </Link>
            ))}
            {user && user !== false ? (
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)}
                className="text-base font-medium">My {user.role === "admin" ? "Admin" : "Dashboard"}</Link>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-base font-semibold">Login</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
